import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import { loadState } from '../../../utils/local-storage';
import { isUserAllowed } from '../../../utils/tab-permissions';
import { StoreSelect } from '../../reusable';

import FiskalyConfigTab from './FiskalyConfigTab';
import PaymentConfigTab from './PaymentConfigTab';
import StoreConfigTab from './StoreConfigTab';

import './onboarding.css';

const scsKeyGroups = [
  'SCS.ALL', 'SCS.ALL.ReadOnly', 'SCS.ASSIST', 'SCS.ASSIST.ReadOnly',
  'SCS.LINK', 'SCS.LINK.ReadOnly', 'SCS.NPOS', 'SCS.NPOS.ReadOnly',
  'SCS.OTHER', 'SCS.OTHER.ReadOnly', 'SCS.POS', 'SCS.POS.ReadOnly',
  'SCS.PROMOTE', 'SCS.PROMOTE.ReadOnly', 'SCS.RL', 'SCS.RL.ReadOnly',
  'SCS.RISE', 'SCS.RISE.ReadOnly', 'SCS.SFS', 'SCS.SFS.ReadOnly',
  'SCS.SIM', 'SCS.SIM.ReadOnly', 'SCS.SCO', 'SCS.SCO.ReadOnly',
  'SCS.TRACK', 'SCS.TRACK.ReadOnly',
];

const TAB_IDS = {
  FISKALY_CONFIG: 'FISKALY_CONFIG',
  PAYMENT_CONFIG: 'PAYMENT_CONFIG',
  STORE_CONFIG: 'STORE_CONFIG',
};

const TAB_LABELS = {
  [TAB_IDS.FISKALY_CONFIG]: 'Fiskaly Config',
  [TAB_IDS.PAYMENT_CONFIG]: 'Payment Config',
  [TAB_IDS.STORE_CONFIG]: 'Store Config',
};

const FRANCE_COUNTRY_VALUE = 'FRA';

const getTabsForCountry = (selectedCountry) => {
  const tabs = [
    { id: TAB_IDS.STORE_CONFIG, label: TAB_LABELS[TAB_IDS.STORE_CONFIG] },
    { id: TAB_IDS.PAYMENT_CONFIG, label: TAB_LABELS[TAB_IDS.PAYMENT_CONFIG] },
  ];
  if (selectedCountry?.value === FRANCE_COUNTRY_VALUE) {
    tabs.push({ id: TAB_IDS.FISKALY_CONFIG, label: TAB_LABELS[TAB_IDS.FISKALY_CONFIG] });
  }
  return tabs;
};

class StoreOpeningMain extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeTab: TAB_IDS.STORE_CONFIG,
      scsGroups: [],
      selectedCountry: loadState('auth')?.country,
    };
  }

  componentDidMount() {
    const userScsGroups = scsKeyGroups.filter(
      (group) => isUserAllowed(this.props.userInfo.groups, group),
    );
    this.setState({ scsGroups: userScsGroups });
  }

  goToNextTab = () => {
    const { activeTab, selectedCountry } = this.state;
    const tabs = getTabsForCountry(selectedCountry);
    const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
    const nextTab = tabs[currentIndex + 1];
    if (nextTab) {
      this.setState({ activeTab: nextTab.id });
    }
  };

selectCountry = (selectedCountry) => {
    this.setState({ activeTab: TAB_IDS.STORE_CONFIG, selectedCountry });
  };

  selectTab = (tabId) => {
    this.setState({ activeTab: tabId });
  };

  renderCountrySelector = () => {
    const { selectedCountry } = this.state;

    return (
      <section className="soa-country-selector">
        <div className="soa-country-picker">
          <StoreSelect
            fullWidth
            noStores
            selectCountry={this.selectCountry}
            selectStore={() => {}}
            selectedCountry={selectedCountry}
          />
        </div>
      </section>
    );
  };

  renderTabs = (tabs) => {
    const { activeTab } = this.state;

    return (
      <div aria-label="Config Sections" className="soa-main-tabs" role="tablist">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              aria-selected={isActive}
              className={`soa-main-tab ${isActive ? 'soa-main-tab-active' : ''}`}
              role="tab"
              type="button"
              onClick={() => this.selectTab(tab.id)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
    );
  };

  renderActiveTab = (tabs) => {
    const { activeTab, selectedCountry } = this.state;
    const { userInfo } = this.props;

    const currentIndex = tabs.findIndex((tab) => tab.id === activeTab);
    const hasNextTab = currentIndex >= 0 && currentIndex < tabs.length - 1;

    if (activeTab === TAB_IDS.FISKALY_CONFIG) {
      return <FiskalyConfigTab selectedCountry={selectedCountry} />;
    }

    if (activeTab === TAB_IDS.PAYMENT_CONFIG) {
      return (
        <PaymentConfigTab
          hasNextTab={hasNextTab}
          selectedCountry={selectedCountry}
          onSaveAndNext={this.goToNextTab}
        />
      );
    }

    return (
      <StoreConfigTab
        hasNextTab={hasNextTab}
        selectedCountry={selectedCountry}
        userInfo={userInfo}
        onSaveAndNext={this.goToNextTab}
      />
    );
  };

  render() {
    const { scsGroups, selectedCountry } = this.state;

    if (scsGroups.length === 0) {
      return (
        <main className="ncss-col-sm-10">
          <p>You do not have permission to access Store Opening Automation.</p>
        </main>
      );
    }

    if (!selectedCountry) {
      return (
        <main className="ncss-col-sm-10">
          {this.renderCountrySelector()}
          <p className="soa-country-prompt">
            Select a country to continue.
          </p>
        </main>
      );
    }

    const tabs = getTabsForCountry(selectedCountry);

    return (
      <main className="ncss-col-sm-10">
        {this.renderCountrySelector()}
        {this.renderTabs(tabs)}
        {this.renderActiveTab(tabs)}
      </main>
    );
  }
}

StoreOpeningMain.propTypes = {
  userInfo: PropTypes.shape({
    accessToken: PropTypes.string,
    email: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

const mapStateToProps = (state) => ({
  userInfo: state.authorizationReducer.auth,
});

export default connect(mapStateToProps, null)(StoreOpeningMain);
